// Ops page — single pane of glass for "is the system healthy?"
//
// Top:    health summary (jobs, outbox, sessions, DB).
// Bottom: audit log timeline with filters.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type OpsHealth, type AuditEntry } from "../lib/api";

const STATUS_COLOR: Record<string, string> = {
  ok:       "bg-emerald-100 text-emerald-800 border-emerald-300",
  degraded: "bg-amber-100 text-amber-900 border-amber-300",
  failing:  "bg-rose-100 text-rose-900 border-rose-400",
};

export function Ops() {
  const health = useQuery<OpsHealth>({
    queryKey: ["ops-health"],
    queryFn: api.ops.health,
    refetchInterval: 30_000, // auto-refresh every 30s
  });

  if (health.isLoading) return <p>Loading…</p>;
  if (health.error)   return <p className="text-red-700">{(health.error as Error).message}</p>;
  if (!health.data)   return null;
  const h = health.data;

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Ops</h1>
        <div className="text-xs text-slate-500">
          as of {new Date(h.as_of).toLocaleString()} ·
          <button className="underline ml-2" onClick={() => health.refetch()}>refresh</button>
        </div>
      </div>

      <section className={`border-l-4 p-3 rounded ${STATUS_COLOR[h.overall_status]}`}>
        <div className="text-sm font-medium uppercase">{h.overall_status}</div>
        {h.issues.length === 0 ? (
          <p className="text-sm">No issues.</p>
        ) : (
          <ul className="text-sm list-disc list-inside">
            {h.issues.map((iss, i) => <li key={i}>{iss}</li>)}
          </ul>
        )}
      </section>

      <section className="grid grid-cols-3 gap-3">
        <KPI label="Outbox pending"        value={h.outbox.pending} />
        <KPI label="Retry exhausted"       value={h.outbox.retry_exhausted} highlight={h.outbox.retry_exhausted > 0} />
        <KPI label="Sent (last 24h)"       value={h.outbox.sent_last_24h} />
        <KPI label="Active sessions"       value={h.sessions.active} />
        <KPI label="DB size"               value={h.database.size_pretty} />
        <KPI label="Last successful send"  value={h.outbox.last_successful_send ? ago(h.outbox.last_successful_send) : "—"} />
      </section>

      <section>
        <h2 className="font-medium mb-2">Scheduled jobs</h2>
        <table className="w-full text-sm">
          <thead className="border-b border-slate-300 text-left">
            <tr>
              <th className="py-2">Job</th>
              <th>Last run</th>
              <th>Status</th>
              <th>Cadence</th>
              <th>Health</th>
            </tr>
          </thead>
          <tbody>
            {h.jobs.map((j) => (
              <tr key={j.name} className="border-b border-slate-100">
                <td className="py-2 font-mono text-xs">{j.name}</td>
                <td className="text-slate-600">{j.last_run ? ago(j.last_run) : "(never)"}</td>
                <td>{statusBadge(j.last_status)}</td>
                <td className="text-slate-500 text-xs">{j.expected_within}</td>
                <td>{j.stale ? <span className="text-amber-700">stale</span> : <span className="text-emerald-700">ok</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <AuditTimeline />
    </div>
  );
}

function AuditTimeline() {
  const [actionPrefix, setActionPrefix] = useState("");
  const [targetTable, setTargetTable] = useState("");

  const audit = useQuery<AuditEntry[]>({
    queryKey: ["audit", actionPrefix, targetTable],
    queryFn: () => api.audit.list({ action_prefix: actionPrefix || undefined, target_table: targetTable || undefined, limit: 50 }),
  });

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-medium">Audit log</h2>
        <div className="flex gap-2 text-sm">
          <input
            className="border rounded px-2 py-1 text-xs"
            placeholder="action prefix (e.g. roster, wins)"
            value={actionPrefix}
            onChange={(e) => setActionPrefix(e.target.value)}
          />
          <input
            className="border rounded px-2 py-1 text-xs"
            placeholder="target_table"
            value={targetTable}
            onChange={(e) => setTargetTable(e.target.value)}
          />
        </div>
      </div>
      {audit.isLoading ? <p>Loading…</p> : null}
      <ul className="space-y-1 text-sm">
        {(audit.data ?? []).map((e) => (
          <li key={e.id} className="flex items-baseline gap-3 border-b border-slate-100 py-1">
            <span className="text-xs text-slate-500 w-32 shrink-0">{new Date(e.at).toLocaleString()}</span>
            <span className="text-xs text-slate-600 w-32 shrink-0">{e.actor_name}</span>
            <span className="font-mono text-xs">{e.action}</span>
            {e.target_table ? <span className="text-xs text-slate-500">{e.target_table}#{e.target_id.slice(0, 8)}</span> : null}
          </li>
        ))}
        {(audit.data ?? []).length === 0 && !audit.isLoading ? (
          <li className="text-sm text-slate-500">No matching audit entries.</li>
        ) : null}
      </ul>
    </section>
  );
}

function KPI({ label, value, highlight }: { label: string; value: number | string; highlight?: boolean }) {
  return (
    <div className={`rounded border p-3 ${highlight ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"}`}>
      <div className="text-xl font-semibold">{value}</div>
      <div className="text-xs text-slate-600 mt-1">{label}</div>
    </div>
  );
}

function statusBadge(s: string) {
  const cls = {
    success: "bg-emerald-100 text-emerald-800",
    failed:  "bg-rose-100 text-rose-800",
    running: "bg-blue-100 text-blue-800",
    "(never)": "bg-slate-100 text-slate-600",
  }[s] ?? "bg-slate-100 text-slate-600";
  return <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{s}</span>;
}

function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
